import { useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Lenis from "lenis";

import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { fetchCollection } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { HeroSection } from "@/components/HeroSection";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { WeeklyShoots } from "@/components/WeeklyShoots";
import { EventPosters } from "@/components/EventPosters";
import { AnnualCalendar } from "@/components/AnnualCalendar";
import { WinnersChronicles } from "@/components/WinnersChronicles";
import { Hemeroteca } from "@/components/Hemeroteca";
import { SocialPostGenerator } from "@/components/SocialPostGenerator";
import { AdminPanel } from "@/components/AdminPanel";
import { Footer } from "@/components/Footer";
import { RegisterSection } from "@/components/RegisterSection";
import { SponsorLogos } from "@/components/SponsorLogos";
import { SupportSection } from "@/components/SupportSection";
import { PhotoGallery } from "@/components/PhotoGallery";
import PaymentResultPage from "@/pages/PaymentResultPage";
import ChroniclePage from "@/pages/ChroniclePage";

function driveThumbnail(url) {
  const match = /\/d\/([^/?]+)/.exec(url || "") || /[?&]id=([^&]+)/.exec(url || "");
  return match ? `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1000` : null;
}

function nextMonday() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  now.setDate(now.getDate() + (8 - (now.getDay() || 7)));
  return [now.getFullYear(), String(now.getMonth() + 1).padStart(2, "0"), String(now.getDate()).padStart(2, "0")].join("-");
}

function calendarEvents(calendar) {
  const from = nextMonday();
  return calendar
    .filter((item) => item.poster_url && item.date >= from)
    .sort((first, second) => first.date.localeCompare(second.date))
    .slice(0, 8)
    .map((item, index) => ({
      id: `cal-${item.id}`,
      title: item.title,
      date: item.date,
      end_date: item.end_date,
      time: item.time || "09:00",
      location: [item.location, item.province].filter(Boolean).join(" · "),
      modality: item.modality,
      status: index === 0 ? "Próxima" : "Convocada",
      poster: driveThumbnail(item.poster_url) || item.poster_url,
      poster_url: item.poster_url,
      results_links: item.results_links || [],
      description: `${item.targets ? `${item.targets} platos · ` : ""}${item.club || ""}`.replace(/ · $/, "").trim(),
      featured: index === 0,
    }));
}

function HomePage() {
  const events = useQuery({ queryKey: ["events"], queryFn: () => fetchCollection("/events") });
  const calendar = useQuery({ queryKey: ["calendar"], queryFn: () => fetchCollection("/calendar"), refetchInterval: 180_000, refetchOnWindowFocus: true });
  const chronicles = useQuery({ queryKey: ["chronicles"], queryFn: () => fetchCollection("/chronicles") });
  const stats = useQuery({ queryKey: ["stats"], queryFn: () => fetchCollection("/stats") });
  const sponsored = useQuery({ queryKey: ["sponsored-active"], queryFn: () => fetchCollection("/sponsored/active") });
  const sponsors = useQuery({ queryKey: ["sponsors"], queryFn: () => fetchCollection("/sponsors") });
  const heroImages = useQuery({ queryKey: ["hero-images"], queryFn: () => fetchCollection("/hero-images") });
  const hasError = events.isError || calendar.isError || chronicles.isError;

  return (
    <div className="App">
      <Navbar />
      {hasError ? (
        <div className="fixed bottom-5 left-5 z-[60] max-w-sm border border-red-500/40 bg-red-950/90 p-4 text-sm text-red-100 shadow-2xl" data-testid="content-load-error">
          No se pudieron cargar algunos contenidos. Revisa la conexión con el servidor.
        </div>
      ) : null}
      <main>
        {heroImages.isLoading ? null : <HeroSection calendar={calendar.data || []} stats={stats.data} sponsored={sponsored.data || []} heroImages={heroImages.data || []} chronicles={chronicles.data || []} />}
        <SponsorLogos sponsors={sponsors.data || []} />
        <EditorialMarquee />
        <WeeklyShoots calendar={calendar.data || []} />
        <EventPosters events={(events.data || []).concat(calendarEvents(calendar.data || []))} />
        <WinnersChronicles chronicles={chronicles.data || []} />
        <AnnualCalendar calendar={calendar.data || []} />
        <Hemeroteca />
        <PhotoGallery />
        <SocialPostGenerator chronicles={chronicles.data || []} />
        <RegisterSection />
        {stats.data?.payments_enabled ? <SupportSection /> : null}
        <AdminPanel />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.08, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/cronica/:slug" element={<ChroniclePage />} />
          <Route path="/patrocinio/exito" element={<PaymentResultPage />} />
          <Route path="/patrocinio/cancelado" element={<PaymentResultPage cancelled />} />
          <Route path="/patrocinio/enviada" element={<PaymentResultPage submitted />} />
          <Route path="/apoyo/gracias" element={<PaymentResultPage support />} />
          <Route path="/apoyo/cancelado" element={<PaymentResultPage support cancelled />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
